import { Box, Text, useBreakpointValue } from '@chakra-ui/react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { IoMdCloseCircle } from 'react-icons/io';
import { useNavigate } from 'react-router';
import SuccessCheck from '@/assets/icons/SuccessCheck';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import type { FaceliftDialogProps } from '@/components/Atoms/FaceliftDialog/FaceliftDialog.types';
import Show from '@/components/Molecules/Show';
import { colors } from '@/components/ui/theme';

export interface FaceliftMessageProps
	extends Omit<FaceliftDialogProps, 'children'> {
	messageSx?: React.CSSProperties;
	error?: boolean;
	messageTitle?: string;
	messageDescription?: string;
	goBack?: string;
	customIcon?: ReactNode;
	showCloseBtn?: boolean;
	timeout?: number;
	onClose?: () => void;
}

const FaceliftMessage = ({
	error = false,
	goBack,
	messageSx,
	message,
	messageTitle,
	customIcon,
	showCloseBtn = false,
	bg = 'brand.messageBg',
	timeout = 2000,
	onClose,
	...rest
}: FaceliftMessageProps) => {
	const navigate = useNavigate();
	const title = messageTitle || (error ? 'Error!' : 'Success!');
	const [modalVisible, setModalVisible] = useState(false);
	const messageIcon = customIcon || <SuccessCheck />;

	const dialogWidth = useBreakpointValue({
		base: '90vw',
		md: 450,
	});

	const handleClose = useCallback(() => {
		setModalVisible(false);
		onClose?.();
		if (goBack) {
			navigate(goBack);
		}
	}, [goBack, navigate, onClose]);

	useEffect(() => {
		if (message) {
			setModalVisible(true);
			// Only set auto-close timer if timeout is provided and greater than 0
			if (timeout && timeout > 0) {
				const timer = setTimeout(handleClose, timeout);
				return () => clearTimeout(timer);
			}
		} else {
			// Close modal when message is cleared
			setModalVisible(false);
		}
	}, [message, timeout, handleClose]);

	return (
		<FaceliftDialog
			bg={bg}
			open={modalVisible}
			showCloseBtn={showCloseBtn}
			onOpenChange={(open) => {
				setModalVisible(open);
				if (!open) {
					onClose?.();
				}
			}}
			sx={{
				borderRadius: 15,
				width: dialogWidth,
			}}
			{...rest}
		>
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				textAlign="center"
				py={2}
				style={messageSx}
			>
				<Box mb={4}>
					<Show when={!error}>{messageIcon}</Show>
					<Show when={error}>
						<IoMdCloseCircle color={colors.errors} size={64} />
					</Show>
				</Box>
				<Box mb={4}>
					<Text fontSize={32} fontWeight={700}>
						{title}
					</Text>
				</Box>
				<Box>
					<Text fontSize="md" fontWeight={400}>
						{message}
					</Text>
				</Box>
			</Box>
		</FaceliftDialog>
	);
};

export default FaceliftMessage;
